"use client";
import { Track } from "@/src/lib/tracking";

import { useEffect, useRef } from "react";

import { useShellRouter } from "@hooks/useShellAwareRouter";
import { useSnapshot } from "valtio";
import { ROUTER_PATH } from "@config/router";
import { isProtectedRoute } from "../config/protectedRoutes";
import { rootStore } from "@store/rootStore";
import { usePathname } from "@hooks/usePathname";

/**
 * Hook to protect routes that require authentication
 * Should be used in root layout component
 *
 * @example
 * ```tsx
 * export function RootLayoutClient({ children }) {
 *   useRouteProtection();
 *   return <>{children}</>;
 * }
 * ```
 */
export const useRouteProtection = () => {
  const router = useShellRouter();
  const pathname = usePathname(true);
  const { isLoading, isLoggedIn, isFetching } = useSnapshot(rootStore.login);
  const redirectedRef = useRef(false);

  useEffect(() => {
    // Reset redirect flag when pathname changes
    redirectedRef.current = false;
  }, [pathname]);

  useEffect(() => {
    // Skip if not a protected route
    if (!isProtectedRoute(pathname)) {
      return;
    }

    // Prevent multiple redirects
    if (redirectedRef.current) {
      return;
    }

    // Wait for login state to be determined
    if (isLoading || isFetching) {
      return;
    }

    // Redirect to home if not logged in
    // 排除邀请页面：/aura/[handle] 和 /task/invite/[inviteCode]
    const isInvitePage =
      pathname.includes("/aura/") || pathname.includes("/task/invite/");
    if (!isLoggedIn && !isInvitePage) {
      redirectedRef.current = true;
      Track.log(
        "info",
        "useRouteProtection",
        "User not logged in, redirecting from",
        { arg1: String(pathname), arg2: String("to home") },
      );
      router.replace(ROUTER_PATH.HOME);
      return;
    }
  }, [isLoading, isLoggedIn, pathname, router, isFetching]);
};
